import * as THREE from 'three';

// ── THE CITY, IN ~30 DRAW CALLS INSTEAD OF ~1,350 ──────────────────────────────
//
// The city GLB is cheap in triangles (~31k) and ruinous in objects: 1,357 meshes
// on ~22 materials. Every mesh is its own draw call, its own frustum test and its
// own uniform upload, so the overhead view behind the landing page drew 1,578
// calls a frame and the street ~500 (measured 2026-09-23). The GPU was idle; the
// CPU was busy telling it about one wall at a time.
//
// Merging by material was deferred once because the WORLD NEEDS THE NODE GRAPH:
// venue anchors (`LM_*`, `Filler_*`), the hero Lotus, the painted signs, the
// ?pick=1 inspector, the building brush and the ground stencil all find meshes
// BY NAME. So this does not merge the graph — it leaves every original node
// exactly where it was, still named, still raycastable, and only stops DRAWING
// it. A BatchedMesh per (material × vertex layout) draws the same geometry,
// with per-instance frustum culling and per-instance visibility (which is what
// keeps the distance cull working).
//
// Why hiding with `visible = false` is safe here:
//   · three's Raycaster and r3f's event system do not test `visible`, so clicks,
//     picks and the admin raycasters still land on the named originals;
//   · the renderer skips an invisible node's WHOLE SUBTREE — so a mesh is only
//     batched when everything beneath it is batched too. Anything else (the
//     hero car, the video signs, transparent glass) keeps rendering as itself;
//   · materials are SHARED, not copied, so everything that edits a city
//     material in place — the night emissive pulse, GroundPits' stencil — keeps
//     working with no changes.
//
// ?batch=0 turns it off for an A/B.

export const CITY_BATCH_OFF = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('batch') === '0';

// Raycasts must not hit the batch itself — it would report a nameless object in
// place of the building that was actually clicked.
const NO_RAYCAST = () => {};

// Every geometry in a batch must present the same attributes with the same
// layout. GLBs from gltf-transform mix quantised, normalised and interleaved
// attributes freely, so each source is flattened to plain Float32 first. The
// city is 31k triangles; the copy is a rounding error next to the draw calls.
function toPlainFloat(geo, names) {
  const out = new THREE.BufferGeometry();
  for (const name of names) {
    const a = geo.getAttribute(name);
    const n = a.count; const k = a.itemSize;
    const arr = new Float32Array(n * k);
    for (let i = 0; i < n; i++) for (let c = 0; c < k; c++) arr[i * k + c] = a.getComponent(i, c);
    out.setAttribute(name, new THREE.BufferAttribute(arr, k));
  }
  if (geo.index) {
    out.setIndex(new THREE.BufferAttribute(Uint32Array.from(geo.index.array), 1));
  } else {
    const n = geo.getAttribute('position').count;
    const idx = new Uint32Array(n);
    for (let i = 0; i < n; i++) idx[i] = i;
    out.setIndex(new THREE.BufferAttribute(idx, 1));
  }
  return out;
}

// One material slot of a multi-material mesh → its own index range.
function sliceGroup(plain, group) {
  const g = new THREE.BufferGeometry();
  for (const name of Object.keys(plain.attributes)) g.setAttribute(name, plain.getAttribute(name));
  const idx = plain.index.array.slice(group.start, group.start + group.count);
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  return g;
}

function batchable(o, exclude) {
  if (!o.isMesh || o.isSkinnedMesh || o.isInstancedMesh || o.isBatchedMesh) return false;
  if (!o.geometry?.getAttribute('position')) return false;
  if (o.geometry.morphAttributes && Object.keys(o.geometry.morphAttributes).length) return false;
  if (exclude(o)) return false;
  const mats = Array.isArray(o.material) ? o.material : [o.material];
  // Transparent surfaces stay as themselves: three sorts transparent objects by
  // OBJECT position, and a batch's position is the city's origin — glass drawn
  // in the wrong order is worse than a few extra calls.
  return mats.every((m) => m && !m.transparent);
}

/**
 * Build draw batches for a loaded city model.
 * @param {THREE.Object3D} root  the model root (its own transform may be anything)
 * @param {(o: THREE.Object3D) => boolean} exclude  nodes that must keep rendering
 *        as themselves (moved at runtime, re-materialed, animated)
 * @returns {{ batches: THREE.BatchedMesh[], slots: Map<THREE.Object3D, Array<[THREE.BatchedMesh, number]>>, hidden: THREE.Object3D[], stats: object } | null}
 */
export function buildCityBatches(root, exclude = () => false) {
  root.updateMatrixWorld(true);
  const toRoot = new THREE.Matrix4().copy(root.matrixWorld).invert();

  // Pass 1 — which meshes can go, and which subtrees are ENTIRELY batchable.
  // A node may only be hidden if nothing that still needs drawing lives under it.
  const ok = new Set();
  const clean = new Map();   // node → whole subtree batchable?
  const visit = (o) => {
    let all = o.isMesh ? batchable(o, exclude) : true;
    if (!o.isMesh && (o.isLight || o.isSprite || o.isPoints || o.isLine)) all = false;
    for (const c of o.children) if (!visit(c)) all = false;
    clean.set(o, all);
    if (o.isMesh && all) ok.add(o);
    return all;
  };
  visit(root);
  if (!ok.size) return null;

  // Pass 2 — bucket by material + vertex layout.
  const buckets = new Map();
  for (const o of ok) {
    const geo = o.geometry;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    const names = Object.keys(geo.attributes).filter((n) => n !== 'tangent' || mats.some((m) => m.normalMap)).sort();
    const parts = Array.isArray(o.material) && geo.groups.length
      ? geo.groups.map((g) => ({ mat: mats[g.materialIndex], group: g })).filter((p) => p.mat)
      : [{ mat: mats[0], group: null }];
    for (const p of parts) {
      const key = `${p.mat.uuid}|${names.join(',')}`;
      let b = buckets.get(key);
      if (!b) { b = { mat: p.mat, names, items: [] }; buckets.set(key, b); }
      b.items.push({ o, group: p.group });
    }
  }

  // Pass 3 — one BatchedMesh per bucket. Shared source geometries (the same
  // building placed ten times) are added ONCE and instanced.
  const batches = [];
  const slots = new Map();
  const plainCache = new Map();
  let instances = 0;
  for (const b of buckets.values()) {
    const geoms = new Map(); // src geometry + group → prepared geometry
    let verts = 0; let idx = 0;
    for (const it of b.items) {
      const gk = `${it.o.geometry.uuid}:${it.group ? it.group.start : 'all'}`;
      if (geoms.has(gk)) continue;
      let plain = plainCache.get(`${it.o.geometry.uuid}|${b.names.join(',')}`);
      if (!plain) { plain = toPlainFloat(it.o.geometry, b.names); plainCache.set(`${it.o.geometry.uuid}|${b.names.join(',')}`, plain); }
      const g = it.group ? sliceGroup(plain, it.group) : plain;
      geoms.set(gk, { g, id: -1 });
      verts += g.getAttribute('position').count;
      idx += g.index.count;
    }
    const batch = new THREE.BatchedMesh(b.items.length, verts, idx, b.mat);
    batch.name = `cityBatch:${b.mat.name || b.mat.type}`;
    batch.raycast = NO_RAYCAST;
    batch.userData.cityBatch = true;
    for (const rec of geoms.values()) rec.id = batch.addGeometry(rec.g);
    const m = new THREE.Matrix4();
    for (const it of b.items) {
      const rec = geoms.get(`${it.o.geometry.uuid}:${it.group ? it.group.start : 'all'}`);
      const inst = batch.addInstance(rec.id);
      m.multiplyMatrices(toRoot, it.o.matrixWorld);
      batch.setMatrixAt(inst, m);
      if (!slots.has(it.o)) slots.set(it.o, []);
      slots.get(it.o).push([batch, inst]);
      instances++;
    }
    batches.push(batch);
  }
  // Slices share the plain geometry's attributes; the batch has copied what it
  // needs, so the intermediates can go.
  for (const g of plainCache.values()) g.dispose();

  // Hide the originals the batches now draw. Only the TOP of each clean subtree
  // needs the flag, and hiding it there also spares the renderer the walk.
  //
  // They are ALSO frozen: a hidden city is static geometry, and three was still
  // recomposing every one of its ~3,000 node matrices every frame (the top line
  // of the fly-in profile once the draw calls were gone). World matrices were
  // computed above against the root's final transform, so they stay correct;
  // getObjectByName / getWorldPosition / Box3.setFromObject read them as before.
  // The hero car was sized before this ran (CityModel), so nothing is frozen
  // mid-change.
  const hidden = [];
  const hide = (o) => {
    if (clean.get(o) && (o.isMesh || o.children.length)) {
      if (o !== root) {
        o.visible = false;
        o.matrixWorldAutoUpdate = false;
        o.traverse((d) => { d.matrixAutoUpdate = false; });
        hidden.push(o);
        return;
      }
    }
    for (const c of o.children) hide(c);
  };
  hide(root);

  return { batches, slots, hidden, stats: { meshes: ok.size, batches: batches.length, instances } };
}

/** Undo buildCityBatches: show the originals again and free the batch buffers. */
export function disposeCityBatches(res) {
  if (!res) return;
  for (const o of res.hidden) {
    o.visible = true;
    o.matrixWorldAutoUpdate = true;
    o.traverse((d) => { d.matrixAutoUpdate = true; });
  }
  for (const b of res.batches) { b.removeFromParent(); b.dispose(); }
}
